import React from 'react';
import {
  Paper,
  Typography,
  Box,
  
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { DevelopmentOption, TaxAnalysis } from '../types/api.types';

interface FinancialSummaryProps {
  options: DevelopmentOption[];
  taxAnalysis?: TaxAnalysis;
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({ options, taxAnalysis }) => {
  // Early return if no options available
  if (!options || !Array.isArray(options) || options.length === 0) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Financial Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Financial analysis will be available once development options are calculated.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // Sort options by ROI for the comparison table
  const sortedOptions = [...(options || [])].sort((a, b) => 
    (b.financial_metrics?.roi || 0) - (a.financial_metrics?.roi || 0)
  );

  // Calculate summary metrics with null safety
  const bestROI = Math.max(...options.map(opt => opt.financial_metrics?.roi || 0));
  const bestCapRate = Math.max(...options.map(opt => opt.financial_metrics?.cap_rate || 0));
  const shortestPayback = Math.min(...options
    .filter(opt => opt.financial_metrics?.payback_period && opt.financial_metrics.payback_period > 0)
    .map(opt => opt.financial_metrics!.payback_period));
  const highestRevenue = Math.max(...options.map(opt => opt.financial_metrics?.revenue_projection || 0));

  const getROIColor = (roi: number) => {
    if (roi >= 0.25) return 'success';
    if (roi >= 0.15) return 'primary';
    if (roi >= 0.05) return 'warning';
    return 'error';
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Financial Analysis Summary
      </Typography>

      {/* Key Financial Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon />
                <Typography variant="h6">Best ROI</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatPercent(bestROI)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                {sortedOptions[0]?.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssessmentIcon />
                <Typography variant="h6">Best Cap Rate</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatPercent(bestCapRate)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Annual return on investment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'secondary.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon />
                <Typography variant="h6">Max Revenue</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(highestRevenue)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Highest projected revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReceiptIcon />
                <Typography variant="h6">Payback</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {isFinite(shortestPayback) ? `${shortestPayback.toFixed(1)} yrs` : 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Fastest return of investment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Comparison Table */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Development Options Financial Comparison
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Option</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Annual NOI</TableCell>
                <TableCell align="right">ROI</TableCell>
                <TableCell align="right">Cap Rate</TableCell>
                <TableCell align="right">Payback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOptions.map((option) => (
                <TableRow key={option.scenario} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={`${option.units} Units`}
                          size="small"
                          variant="outlined"
                        />
                        {!option.feasible && (
                          <Chip
                            label="Not Feasible"
                            size="small"
                            color="error"
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(option.cost_breakdown?.total_cost || option.financial_metrics?.total_cost || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(option.financial_metrics?.revenue_projection || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(option.financial_metrics?.annual_noi || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={formatPercent(option.financial_metrics?.roi || 0)}
                      size="small"
                      color={getROIColor(option.financial_metrics?.roi || 0)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatPercent(option.financial_metrics?.cap_rate || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {(option.financial_metrics?.payback_period || 0).toFixed(1)} yrs
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Tax Analysis */}
      {taxAnalysis && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Property Tax Analysis
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Annual Taxes
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(taxAnalysis.current_tax_analysis?.annual_tax || taxAnalysis.current_taxes || 0)}
                </Typography>
                {taxAnalysis.current_tax_analysis && (
                  <Typography variant="caption" color="text.secondary">
                    Rate: {taxAnalysis.current_tax_analysis.effective_rate}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {taxAnalysis.future_projections ? 'Future Tax Projections' : 'Projected Taxes by Scenario'}
                </Typography>
                {taxAnalysis.future_projections ? (
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    {taxAnalysis.future_projections.projections.slice(0, 3).map((proj) => (
                      <Grid item xs={4} key={proj.year}>
                        <Typography variant="caption" color="text.secondary">
                          Year {proj.year}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatCurrency(proj.projected_annual_tax)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                ) : taxAnalysis.projected_taxes ? (
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Renovation
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatCurrency(taxAnalysis.projected_taxes.renovation || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Extension
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatCurrency(taxAnalysis.projected_taxes.extension || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Teardown
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatCurrency(taxAnalysis.projected_taxes.teardown || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No projection data available
                  </Typography>
                )}
              </Box>
            </Grid>
            {/* Show recommendations if available */}
            {taxAnalysis.recommendations && taxAnalysis.recommendations.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Tax Planning Recommendations:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {taxAnalysis.recommendations.slice(0, 3).map((rec, index) => (
                      <Typography key={index} component="li" variant="body2">
                        {rec}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
            {/* Show legacy tax implications if available */}
            {taxAnalysis.tax_implications && taxAnalysis.tax_implications.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Tax Implications:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {taxAnalysis.tax_implications.map((implication, index) => (
                      <Typography key={index} component="li" variant="body2">
                        {implication}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default FinancialSummary;